import { supabase } from '../lib/supabase';
import type { FilaJukebox, FilaJukeboxStatus } from '../models/jukebox.model';

export interface NovoPedidoJukeboxDTO {
  telaId:           string;
  empresaId:        string;
  youtubeVideoId:   string;
  titulo:           string;
  thumbnail:        string;
  sessionId:        string;
  vibe?:            string;
  duracao_segundos?: number | null;
  clienteToken?:      string | null;
}

/**
 * jukeboxService — adaptador Supabase canônico para `fila_jukebox`.
 * Consumido pelo Player (via playerRepository) e pela feature Jukebox
 * (via jukeboxRepository thin wrapper).
 */
export const jukeboxService = {
  async inserirPedido(dto: NovoPedidoJukeboxDTO): Promise<FilaJukebox> {
    const { data, error } = await supabase.rpc('inserir_pedido_jukebox' as never, {
      p_tela_id:            dto.telaId,
      p_empresa_id:         dto.empresaId,
      p_youtube_video_id:   dto.youtubeVideoId,
      p_titulo:             dto.titulo,
      p_thumbnail:          dto.thumbnail,
      p_session_id:         dto.sessionId,
      p_vibe:               dto.vibe ?? 'neutro',
      p_duracao_segundos:   dto.duracao_segundos ?? null,
      p_cliente_token:      dto.clienteToken ?? null,
    } as never);

    if (error) throw error;
    return data as FilaJukebox;
  },

  async listarAtivos(telaId: string): Promise<FilaJukebox[]> {
    const { data, error } = await supabase.rpc('jukebox_listar_fila' as never, {
      p_tela_id: telaId,
    } as never);

    if (error) throw error;
    return (data ?? []) as FilaJukebox[];
  },

  async buscarTocandoAgora(telaId: string): Promise<FilaJukebox | null> {
    const { data, error } = await supabase.rpc('jukebox_tocando_agora' as never, {
      p_tela_id: telaId,
    } as never);

    if (error) throw error;
    return (data ?? null) as FilaJukebox | null;
  },

  async atualizarStatus(
    id: string,
    status: FilaJukeboxStatus,
    telaId: string,
  ): Promise<void> {
    const { error } = await supabase.rpc('atualizar_status_jukebox' as never, {
      p_pedido_id:   id,
      p_novo_status: status,
      p_tela_id:     telaId,
    } as never);

    if (error) throw error;
  },

  async buscarCooldownStatus(
    telaId: string,
  ): Promise<Array<{ youtube_video_id: string; restante_ms: number }>> {
    const { data, error } = await supabase
      .rpc('cooldown_status', { p_tela_id: telaId } as never);

    if (error) throw error;
    return (data ?? []) as Array<{ youtube_video_id: string; restante_ms: number }>;
  },

  async limparFilaForaDaPlaylist(telaId: string): Promise<number> {
    // Ids válidos são derivados no servidor (telas.playlist_id → playlist_itens);
    // o cliente só sinaliza — ver migration_zz_limpar_fila_server_side.sql.
    const { data, error } = await supabase.rpc('limpar_fila_fora_playlist' as never, {
      p_tela_id: telaId,
    } as never);

    if (error) throw error;
    return (data as number) ?? 0;
  },

  async resolverTocandoDuplicados(telaId: string): Promise<void> {
    const { error } = await supabase.rpc('resolver_tocando_duplicados' as never, {
      p_tela_id: telaId,
    } as never);

    if (error) throw error;
  },
};
